import { useState } from "react";
import { Play } from "lucide-react";
import { useTranslation } from "react-i18next";

interface VimeoFacadeProps {
  videoId: string;
  poster: string;
}

/**
 * Click-to-play Vimeo facade: nothing is requested from Vimeo until the
 * user clicks the play button. On click, the player iframe is mounted
 * with autoplay=1 so playback starts immediately.
 */
export function VimeoFacade({ videoId, poster }: VimeoFacadeProps) {
  const { t } = useTranslation();
  const [activated, setActivated] = useState(false);

  if (activated) {
    return (
      <div className="relative w-full rounded-2xl overflow-hidden border border-border shadow-clinical-lg bg-black" style={{ aspectRatio: "16 / 9" }}>
        <iframe
          src={`https://player.vimeo.com/video/${videoId}?autoplay=1&title=0&byline=0&portrait=0`}
          className="absolute inset-0 w-full h-full"
          frameBorder="0"
          allow="autoplay; fullscreen; picture-in-picture"
          allowFullScreen
          title={t("video.title")}
        />
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={() => setActivated(true)}
      className="group relative block w-full rounded-2xl overflow-hidden border border-border shadow-clinical-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
      style={{ aspectRatio: "16 / 9" }}
      aria-label={t("video.playLabel")}
    >
      <img
        src={poster}
        alt=""
        aria-hidden="true"
        loading="lazy"
        decoding="async"
        className="absolute inset-0 w-full h-full object-cover"
      />
      <span
        aria-hidden="true"
        className="absolute inset-0 bg-black/10 group-hover:bg-black/20 transition-colors"
      />
      <span className="absolute inset-0 flex items-center justify-center">
        <span className="flex h-16 w-16 lg:h-20 lg:w-20 items-center justify-center rounded-full bg-white shadow-clinical-lg transition-transform duration-200 group-hover:scale-105">
          <Play className="ml-1 text-primary" size={30} fill="currentColor" strokeWidth={0} />
        </span>
      </span>
    </button>
  );
}
